import { useState, useEffect, useCallback } from "react";
import { Lock, RefreshCw, Download, Inbox, Filter, LogOut, ClipboardList, Building2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { STATUS_LABELS, KIND_LABELS } from "../data/content";
import ListingsManager from "./ListingsManager";
import ContentManager from "./ContentManager";

const STORAGE_KEY = "sd_admin_passcode";

function VisitsChart({ series }) {
  const W = 720, H = 150, P = 10;
  const max = Math.max(1, ...series.map((s) => s.views));
  const step = (W - 2 * P) / Math.max(1, series.length - 1);
  const pts = series.map((s, i) => [P + i * step, H - P - (s.views / max) * (H - 2 * P - 10)]);
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `${P},${H - P} ${line} ${W - P},${H - P}`;
  const fmtDay = (d) => d?.slice(8, 10) + "/" + d?.slice(5, 7);
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-navy-surface/60 p-5" data-testid="admin-visits-chart">
      <p className="text-xs uppercase tracking-wide text-slate-400">Visites — 30 derniers jours</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 h-36 w-full" preserveAspectRatio="none">
        <polygon points={area} fill="rgba(212,175,55,0.12)" />
        <polyline points={line} fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinejoin="round" />
        {pts.map((p, i) => series[i].views > 0 && <circle key={i} cx={p[0]} cy={p[1]} r="3" fill="#D4AF37" />)}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-slate-500">
        <span>{fmtDay(series[0]?.day)}</span>
        <span>{fmtDay(series[series.length - 1]?.day)}</span>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  nouveau: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  prelancement: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  a_recontacter: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  presentation: "bg-purple-500/15 text-purple-300 border-purple-400/30",
  offre_a_preciser: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  a_traiter: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  en_cours: "bg-indigo-500/15 text-indigo-300 border-indigo-400/30",
  rendez_vous: "bg-purple-500/15 text-purple-300 border-purple-400/30",
  accepte: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  app_ouverte: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  refuse: "bg-red-500/15 text-red-300 border-red-400/30",
  termine: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  archive: "bg-slate-500/15 text-slate-300 border-slate-400/30",
};

export default function Admin() {
  const [passcode, setPasscode] = useState(sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(STORAGE_KEY));
  const [input, setInput] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [section, setSection] = useState("requests");

  const load = useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.adminStats(passcode),
        api.adminRequests(passcode, { ...(kindFilter && { kind: kindFilter }), ...(statusFilter && { status: statusFilter }) }),
      ]);
      setStats(s);
      setRows(r.requests);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); toast.error("Session expirée."); }
    } finally { setLoading(false); }
  }, [passcode, kindFilter, statusFilter]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await api.adminLogin(input);
      sessionStorage.setItem(STORAGE_KEY, input);
      setPasscode(input);
      setAuthed(true);
    } catch { toast.error("Code administrateur invalide."); }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false); setPasscode(""); setInput(""); setRows([]); setStats(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.adminUpdate(passcode, id, status);
      toast.success("Statut mis à jour.");
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
    } catch { toast.error("Erreur de mise à jour."); }
  };

  const openDetail = async (id) => {
    try { const doc = await api.adminRequest(passcode, id); setSelected(doc); }
    catch { toast.error("Impossible d'ouvrir la demande."); }
  };

  const downloadCV = (att) => {
    const link = document.createElement("a");
    link.href = `data:${att.content_type};base64,${att.data_base64}`;
    link.download = att.filename || "cv";
    link.click();
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5 pt-24" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-white/10 bg-navy-surface/60 p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold"><Lock className="h-5 w-5" /></span>
          <h1 className="mt-6 font-serif text-3xl text-white">Espace Gaëlle</h1>
          <p className="mt-2 text-sm text-slate-400">Tableau de suivi des demandes du site.</p>
          <input
            data-testid="admin-passcode-input"
            type="password" placeholder="Code administrateur" value={input}
            onChange={(e) => setInput(e.target.value)}
            className="mt-6 w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
          />
          <button data-testid="admin-login-btn" type="submit" className="mt-4 w-full rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-gold-light">
            Accéder au tableau de suivi
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 pb-20 pt-28 lg:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-gold">Espace Gaëlle</p>
            <h1 className="mt-2 font-serif text-4xl tracking-tight text-white">Tableau de suivi</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:border-gold/50">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </button>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-200 hover:border-red-400/50">
              <LogOut className="h-4 w-4" /> Quitter
            </button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="mt-8 flex gap-3">
          <button onClick={() => setSection("requests")} data-testid="section-requests"
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${section === "requests" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <ClipboardList className="h-4 w-4" /> Demandes
          </button>
          <button onClick={() => setSection("annonces")} data-testid="section-annonces"
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${section === "annonces" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <Building2 className="h-4 w-4" /> Annonces
          </button>
          <button onClick={() => setSection("contenu")} data-testid="section-contenu"
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${section === "contenu" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <FileText className="h-4 w-4" /> Contenu du site
          </button>
        </div>

        {section === "annonces" && (
          <div className="mt-8"><ListingsManager passcode={passcode} /></div>
        )}
        {section === "contenu" && (
          <div className="mt-8"><ContentManager passcode={passcode} /></div>
        )}
        {section === "requests" && (
        <>
        {/* Visit stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="admin-visit-stats">
          {[
            ["Visites totales", stats?.visits?.total, true],
            ["Visiteurs uniques", stats?.visits?.unique, false],
            ["Aujourd'hui", stats?.visits?.today, false],
            ["7 derniers jours", stats?.visits?.last_7d, false],
            ["30 derniers jours", stats?.visits?.last_30d, false],
          ].map(([label, value, gold]) => (
            <div key={label} className={`rounded-2xl border p-5 ${gold ? "border-gold/25 bg-navy-elevated" : "border-white/10 bg-navy-surface/60"}`}>
              <p className="text-xs text-slate-400">{label}</p>
              <p className={`mt-1 font-serif text-4xl ${gold ? "text-gold" : "text-white"}`}>{value ?? 0}</p>
            </div>
          ))}
        </div>
        {(stats?.visits?.top_pages?.length ?? 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="admin-top-pages">
            <span className="text-xs uppercase tracking-wide text-slate-500">Pages les plus vues :</span>
            {stats.visits.top_pages.map((p) => (
              <span key={p.path} className="rounded-full border border-white/10 bg-navy px-3 py-1 text-xs text-slate-300">
                {p.path === "/" ? "Accueil" : p.path} · {p.views}
              </span>
            ))}
          </div>
        )}
        {stats?.visits?.per_day && <VisitsChart series={stats.visits.per_day} />}

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-2xl border border-gold/25 bg-navy-elevated p-5">
            <p className="text-xs text-slate-400">Total</p>
            <p className="mt-1 font-serif text-4xl text-gold">{stats?.total ?? 0}</p>
          </div>
          {Object.entries(KIND_LABELS).map(([k, label]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-navy-surface/60 p-5">
              <p className="text-xs text-slate-400">{label}</p>
              <p className="mt-1 font-serif text-4xl text-white">{stats?.by_kind?.[k] ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm text-slate-400"><Filter className="h-4 w-4" /> Filtrer</span>
          <select data-testid="filter-kind" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
            className="rounded-full border border-white/10 bg-navy px-4 py-2 text-sm text-white focus:border-gold focus:outline-none">
            <option value="" className="bg-navy">Tous les types</option>
            {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-navy">{l}</option>)}
          </select>
          <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-full border border-white/10 bg-navy px-4 py-2 text-sm text-white focus:border-gold focus:outline-none">
            <option value="" className="bg-navy">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-navy">{l}</option>)}
          </select>
        </div>

        {/* List */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-slate-500">
              <Inbox className="h-10 w-10" /><p>Aucune demande pour ces filtres.</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm" data-testid="admin-table">
              <thead className="bg-navy-surface/60 text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-5 py-4">Référence</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Statut</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.reference}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 font-mono text-gold">{r.reference}</td>
                    <td className="px-5 py-4 text-slate-200">{KIND_LABELS[r.kind] || r.kind}</td>
                    <td className="px-5 py-4 text-slate-300">{r.data?.nom || "—"}<span className="block text-xs text-slate-500">{r.data?.email}</span></td>
                    <td className="px-5 py-4 text-slate-400">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`status-select-${r.reference}`}
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs ${STATUS_COLORS[r.status] || ""}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-navy text-white">{l}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => openDetail(r.id)} data-testid={`view-${r.reference}`} className="text-sm text-gold hover:underline">Voir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={() => setSelected(null)} data-testid="admin-detail">
          <div className="h-full w-full max-w-lg overflow-y-auto border-l border-white/10 bg-navy-surface p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-gold">{selected.reference}</span>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white">Fermer</button>
            </div>
            <h3 className="mt-4 font-serif text-3xl text-white">{KIND_LABELS[selected.kind] || selected.kind}</h3>
            <span className={`mt-3 inline-block rounded-full border px-3 py-1 text-xs ${STATUS_COLORS[selected.status]}`}>{STATUS_LABELS[selected.status]}</span>

            <div className="mt-6 space-y-2 rounded-2xl border border-white/10 bg-navy p-5 text-sm">
              {Object.entries(selected.data || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/5 py-1.5 last:border-0">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-right text-slate-200">{Array.isArray(v) ? v.join(", ") : typeof v === "object" && v ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>

            {selected.attachments?.length > 0 && (
              <div className="mt-6">
                <p className="text-xs uppercase tracking-wide text-gold">Pièces jointes</p>
                {selected.attachments.map((a, i) => (
                  <button key={i} onClick={() => downloadCV(a)} data-testid="download-cv"
                    className="mt-3 flex w-full items-center gap-3 rounded-xl border border-white/10 bg-navy p-4 text-left hover:border-gold/50">
                    <Download className="h-5 w-5 text-gold" />
                    <span className="min-w-0"><span className="block truncate text-sm text-white">{a.filename}</span>
                      <span className="text-xs text-slate-500">{(a.size / 1024).toFixed(0)} Ko</span></span>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-6">
              <p className="text-xs uppercase tracking-wide text-gold">Historique</p>
              <ul className="mt-3 space-y-2">
                {(selected.history || []).map((h, i) => (
                  <li key={i} className="text-sm text-slate-400">
                    <span className="text-gold">•</span> {h.event} <span className="text-xs text-slate-600">— {new Date(h.at).toLocaleString("fr-FR")}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
